<template>
  <div class="visit-date-strip">
    <div v-if="readonly">
      <label class="form-label gray-label">{{ $t('visit_date') }} *</label>
      <div>{{ visit.readableStart }}</div>
    </div>
    <div v-else>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="d-flex align-items-center gap-2 pointer"
             @click="toggleCalendar">
          <span class="gray-label form-label mb-0">{{ monthLabel }}</span>
          <CalendarSvg class="svg-gray svg-size-lg"/>
        </div>
        <div class="d-flex align-items-center gap-0"
             v-if="!readonly">
          <a class="pointer calendar-nav-btn" @click="prevWeek">
            <LeftSvg/>
          </a>
          <a class="pointer calendar-nav-btn" @click="nextWeek">
            <RightSvg/>
          </a>
        </div>
      </div>
      <div class="position-relative">
        <div v-if="show"
             v-click-outside="clickedOutside"
             class="visit-date-strip-calendar">
          <VDatePicker :locale="{ id: lang, firstDayOfWeek: 2, masks: { L: 'DD.MM.YYYY' } }"
                       @dayclick="dayClicked"
                       v-model="calendarValue"/>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <div v-for="(d, dind) in days"
               :key="`date-strip-${dind}`"
               class="visit-date-strip-day text-center pointer"
               :class="{ active: isSelected(d), disabled: readonly }"
               @click="selectDay(d)">
            <div class="visit-date-strip-day-name">{{ dayName(d) }}</div>
            <div class="visit-date-strip-day-num">{{ dayNumber(d) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, getCurrentInstance, ref, watch} from "vue"
import moment from "moment"
import {DatePicker} from 'v-calendar'
import CalendarSvg from '@/assets/svg-vue/calendar/calendar3.svg'
import LeftSvg from '@/assets/svg-vue/general/arrow_left.svg'
import RightSvg from '@/assets/svg-vue/general/arrow_right.svg'
import {useStore} from "vuex"

const VDatePicker = DatePicker

const props = defineProps({
  value: {
    type: String,
    default: null
  },
  readonly: {
    type: Boolean,
    default: false
  },
  visit: {
    type: Object
  }
})

const emit = defineEmits(['input'])
const store = useStore()

const { proxy } = getCurrentInstance()
const lang = computed(() => {
  const supportedLangs = ['ru', 'en', 'uk']
  return supportedLangs.includes(proxy.$i18n.locale) ? proxy.$i18n.locale : 'ru'
})

const windowStart = ref(moment(props.value || undefined).startOf('day'))
const show = ref(false)
const justOpened = ref(false)
const calendarValue = ref(windowStart.value.toDate())
const isMobile = computed(() => store.state.dom.isMobile)

const days = computed(() => {
  const arr = []
  const amountOfDays = isMobile.value ? 7 : 8
  for(let i = 0; i < amountOfDays; i++) {
    arr.push(moment(windowStart.value).add(i, 'days'))
  }
  return arr
})

const monthLabel = computed(() => {
  const first = days.value[0]
  const last = days.value[days.value.length - 1]
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)
  if(first.format('MM') === last.format('MM')) {
    return capitalize(first.format('MMMM'))
  }
  return `${capitalize(first.format('MMMM'))}-${last.format('MMMM')}`
})

const dayName = (d) => d.format('dd')
const dayNumber = (d) => d.format('D')
const isSelected = (d) => props.value && d.format('YYYY-MM-DD') === props.value

const selectDay = (d) => {
  if(props.readonly) return
  emit('input', d.format('YYYY-MM-DD'))
}

const prevWeek = () => {
  windowStart.value = moment(windowStart.value).subtract(7, 'days')
}
const nextWeek = () => {
  windowStart.value = moment(windowStart.value).add(7, 'days')
}

const toggleCalendar = () => {
  if(props.readonly) return
  show.value = !show.value
  justOpened.value = true
  setTimeout(() => {
    justOpened.value = false
  }, 20)
}

const clickedOutside = () => {
  if(!justOpened.value) show.value = false
}

const dayClicked = (e) => {
  const picked = moment(e.id)
  windowStart.value = moment(picked)
  emit('input', picked.format('YYYY-MM-DD'))
  show.value = false
}

watch(() => props.value, (val) => {
  if(!val) return
  const inWindow = days.value.some(d => d.format('YYYY-MM-DD') === val)
  if(!inWindow) windowStart.value = moment(val)
})
</script>

<style scoped lang="scss">
.visit-date-strip-calendar {
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
}
.calendar-nav-btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  &:hover {
    background: rgba(242, 242, 242, 1);
  }
}
.visit-date-strip-day {
  flex: 1 1 0;
  border-radius: 8px;
  background: $gray-btn;
  padding: 5px 10px;
  min-width: 48px;
  &-name {
    font-size: 12px;
    color: $gray-text2;
    text-transform: lowercase;
  }
  &-num {
    font-weight: bold;
    font-size: 16px;
  }
  &.active {
    background: $blue-color;
    .visit-date-strip-day-name, .visit-date-strip-day-num {
      color: #fff;
    }
  }
  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}
@media screen and (max-width: 768px) {
  .visit-date-strip-day {
    min-width: 36px;
    &-name {
      font-size: 11px;
    }
    &-num {
      font-size: 14px;
    }
  }
}
</style>
